/**
 * PIN rules — the dashboard's mirror of `api/utils/pinService.js`.
 *
 * The API is the authority: every set-PIN path there runs the same checks and
 * will reject a bad PIN regardless of what the browser did. This exists so the
 * person choosing a PIN is told before they submit, and so all four screens
 * that can set one (set-PIN link, Settings, Create Manager, Create
 * Organization) say exactly the same thing. Keep the two files in step.
 */

/** Every new PIN is exactly this many digits. */
export const PIN_LENGTH = 6;

const PIN_PATTERN = new RegExp(`^\\d{${PIN_LENGTH}}$`);

export const PIN_RULE_MESSAGE = `The PIN must be exactly ${PIN_LENGTH} digits.`;

export const PIN_WEAK_MESSAGE =
  'That PIN is too easy to guess. It cannot be the same digit repeated '
  + '(111111) or a run of consecutive digits (123456 or 654321).';

/** Exactly six digits. */
export const isValidPin = (pin) => PIN_PATTERN.test(String(pin ?? '').trim());

/**
 * True when a correctly-shaped PIN is one of the guessable patterns — one digit
 * repeated, or a straight run up or down. A wrong-length PIN is not "weak";
 * that is the length rule's job to report.
 */
export const isWeakPin = (pin) => {
  const value = String(pin ?? '').trim();
  if (!PIN_PATTERN.test(value)) return false;

  const digits = [...value].map(Number);
  if (digits.every((d) => d === digits[0])) return true;
  // Runs are not treated as wrapping, so 890123 is allowed.
  const runs = (step) => digits.every((d, i) => i === 0 || d - digits[i - 1] === step);
  return runs(1) || runs(-1);
};

/**
 * The message to show for this PIN, or null when it is acceptable — so a caller
 * is a single `if` rather than two checks it could forget to keep in step.
 */
export const pinProblem = (pin) => {
  if (!isValidPin(pin)) return PIN_RULE_MESSAGE;
  if (isWeakPin(pin)) return PIN_WEAK_MESSAGE;
  return null;
};
